/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useEffect, useState } from 'react';
import { Loading, Theme, Select, SelectItem } from '@carbon/react';
import PropTypes from 'prop-types';
import { GraphiQLExplorer } from 'stepzen-graphiql';
import { findFirstAppWithAccess } from 'utils/common';
import { useApi } from '../contexts/ApiHandler';

function TestToolGraphQL({ api, apps }) {
  const { apiHandler } = useApi();
  const [endpoint, setEndpoint] = useState('');
  const [endpoints, setEndpoints] = useState([]);
  const [security, setSecurity] = useState({
    userCredentials: {
      username: null,
      password: null
    },
    identification: {
      clientID: null,
      clientSecret: '',
      redirectUri: null,
      externalApiKeys: {}
    },
    flags: {},
    scopes: [],
    authToken: {
      code: '',
      access_token: ''
    }
  });

  const setHeaders = (security) => {
    const config = security.flags;

    const headers = {};

    if (config.requiresBasicAuth) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresOauth && config.authToken?.access_token) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresHttpBearerToken) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresDigestAuth) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresHobaAuth) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresMutualAuth) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresNegotiateAuth) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresVapidAuth) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresScramShaAuth) {
      headers.Authorization = 'not_supported';
    }
    if (config.requiresClientId && config.clientIdLocation === 'header') {
      if (config.clientIdName) {
        headers[config.clientIdName] = security.identification.clientID;
      } else {
        headers['X-IBM-Client-Id'] = security.identification.clientID;
      }
    }
    if (config.requiresClientSecret && config.clientSecretLocation === 'header') {
      if (config.clientSecretName) {
        headers[config.clientSecretName] = security.identification.clientSecret;
      } else {
        headers['X-IBM-Client-Secret'] = security.identification.clientSecret;
      }
    }

    return headers;
  };

  const handleEndpointChange = (event) => {
    const selectedUrl = event.target.value;
    const basePlusPath = '/graphql';
    const fullUrl = apiHandler.buildFullUrl(selectedUrl, basePlusPath);
    setEndpoint(fullUrl);

    const operation = api.paths?.['/graphql']?.post;
    let credentials = null;
    if (!security.identification.clientID && apps && apps.length > 0 && apps[0].credentials && apps[0].credentials.length > 0) {
      const initialApp = findFirstAppWithAccess(apps);
      credentials = initialApp ? initialApp.credentials?.[0] : apps[0].credentials[0];
    }
    const securityConfig = apiHandler.getSecurityConfig(api, operation, null, fullUrl);
    if (securityConfig) {
      setSecurity((prev) => ({
        ...prev,
        identification: {
          ...prev.identification,
          clientID: credentials?.client_id || ''
        },
        flags: securityConfig.flags || {}
      }));
    }
  };

  useEffect(() => {
    if (api) {
      const apiEndpoints = apiHandler.getEndpoints(api, {});
      setEndpoints(apiEndpoints);
      
      if (apiEndpoints?.length > 0) {
        const basePlusPath = '/graphql';
        const fullUrl = apiHandler.buildFullUrl(apiEndpoints[0].url, basePlusPath);
        setEndpoint(fullUrl);

        const operation = api.paths?.['/graphql']?.post;
        let credentials = null;
        if (!security.identification.clientID && apps && apps.length > 0 && apps[0].credentials && apps[0].credentials.length > 0) {
          const initialApp = findFirstAppWithAccess(apps);
          credentials = initialApp ? initialApp.credentials?.[0] : apps[0].credentials[0];
        }
        const securityConfig = apiHandler.getSecurityConfig(api, operation, null, fullUrl);
        if (securityConfig) {
          setSecurity((prev) => ({
            ...prev,
            identification: {
              ...prev.identification,
              clientID: credentials?.client_id || ''
            },
            flags: securityConfig.flags || {}
          }));
        }
      }
    }
  }, [api, apiHandler, apps]);

  const config = {
    endpoint,
    showExplorerHeader: false,
    headers: setHeaders(security)
  };

  return (
    // Force light theme as 'stepzen-graphiql' doesn't support dark mode yet.
    // TODO: remove when dark mode support is added.
    <Theme theme="white">
      <div>
        <div className="graphql-endpoint-selector">
          <div>
            <div className="graphql-endpoint-label">
              Endpoint
            </div>
            <div>
              {endpoints && endpoints.length > 0 ? (
                <Select
                  id="graphql-endpoint-selector"
                  onChange={handleEndpointChange}
                  defaultValue={endpoints[0]?.url || ''}
                  hideLabel
                  size="sm"
                  data-testid="graphql-endpoint-selector"
                  style={{ width: '100%' }}
                >
                  {endpoints.map((endpoint, index) => {
                    const endpointTypesText = endpoint.types ? `${endpoint.types}: ` : '';
                    const displayText = `${endpointTypesText}${endpoint.url}/graphql`;
                    return (
                      <SelectItem
                        key={endpoint.url || `endpoint-${index}`}
                        value={endpoint.url || ''}
                        text={displayText || `Endpoint ${index + 1}`}
                        data-testid="graphql-endpoint-option"
                      />
                    );
                  })}
                </Select>
              ) : (
                <div>No endpoints available</div>
              )}
            </div>
            <div style={{ height: '2rem' }} />
          </div>
        </div>
        
        {endpoint ? <GraphiQLExplorer key={`graphql-explorer-${endpoint}`} config={config} /> : <Loading small data-testid="loading-graphiql-explorer" />}
      </div>
    </Theme>
  );
}

// needed for finding in tests
TestToolGraphQL.displayName = 'TestGraphQL';
TestToolGraphQL.propTypes = {
  api: PropTypes.object,
  options: PropTypes.object,
  apps: PropTypes.array
};

export default TestToolGraphQL;
